import React, { useEffect, useState, useCallback } from "react";
import { UserPlus, Search, Download, Pencil, Ban, CheckCircle2, KeyRound, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { API, apiErr } from "@/lib/api";
import { PageHeader, Card, Empty, DemoTag } from "@/components/ui/page";
import { Pill } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";

const empty = {
  employee_id: "", name: "", email: "", temp_password: "GoSource@123", role: "employee",
  process: "", designation: "", manager_id: "", team_lead_id: "", shift: "", location: "", doj: "", mobile: "", employment_status: "active",
};

export default function Employees() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [fProc, setFProc] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [opts, setOpts] = useState({ processes: [], shifts: [], managers: [], team_leads: [], designations: [] });
  const [modal, setModal] = useState(null); // {mode, data}
  const [pwModal, setPwModal] = useState(null);
  const [delModal, setDelModal] = useState(null);
  const [page, setPage] = useState(1);
  const perPage = 10;

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (fProc) params.process = fProc;
    if (fStatus) params.status = fStatus;
    api.get("/users", { params }).then((r) => setRows(r.data)).catch(() => {});
  }, [q, fProc, fStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/processes"), api.get("/shifts"), api.get("/users/managers"), api.get("/meta/options")]).then(([p, s, m, o]) => {
      setOpts({ processes: p.data, shifts: s.data, managers: m.data.managers, team_leads: m.data.team_leads, designations: o.data.designations });
    });
  }, []);

  const paged = rows.slice((page - 1) * perPage, page * perPage);
  const pages = Math.ceil(rows.length / perPage) || 1;

  const save = async () => {
    const f = modal.data;
    try {
      if (modal.mode === "add") {
        await api.post("/users", { ...f, manager_id: f.manager_id || null, team_lead_id: f.team_lead_id || null });
        toast.success("Employee created");
      } else {
        const { temp_password, ...upd } = f;
        await api.put(`/users/${f.id}`, { ...upd, manager_id: f.manager_id || null, team_lead_id: f.team_lead_id || null });
        toast.success("Employee updated");
      }
      setModal(null);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const toggle = async (u) => {
    const ns = u.employment_status === "active" ? "disabled" : "active";
    try { await api.post(`/users/${u.id}/status`, { employment_status: ns }); toast.success(`Set to ${ns}`); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const resetPw = async () => {
    try { await api.post(`/users/${pwModal.id}/reset-password`, { new_password: pwModal.pw }); toast.success("Password reset"); setPwModal(null); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const deleteUser = async () => {
    try { await api.delete(`/users/${delModal.id}`); toast.success("Employee deleted"); setDelModal(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const exportFile = (fmt) => {
    const token = localStorage.getItem("gs_token");
    fetch(`${API}/export/employees?fmt=${fmt}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = `GoSource_Workforce_Report.${fmt === "xlsx" ? "xlsx" : "csv"}`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div>
      <PageHeader title={isAdmin ? "Employee Master" : "My Team"} subtitle={`${rows.length} records`} testId="employees-header">
        <button data-testid="export-csv" onClick={() => exportFile("csv")} className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> CSV</button>
        <button data-testid="export-xlsx" onClick={() => exportFile("xlsx")} className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> Excel</button>
        {isAdmin && <button data-testid="add-user-btn" onClick={() => setModal({ mode: "add", data: { ...empty } })} className="flex items-center gap-2 rounded-lg bg-[#08A7A0] px-4 py-2 text-sm font-bold text-white hover:bg-[#06827C]"><UserPlus className="h-4 w-4" /> Add User</button>}
      </PageHeader>

      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="emp-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, email or ID…" className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#08A7A0]" />
          </div>
          <select data-testid="emp-filter-process" value={fProc} onChange={(e) => { setFProc(e.target.value); setPage(1); }} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option value="">All Processes</option>
            {opts.processes.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
          <select data-testid="emp-filter-status" value={fStatus} onChange={(e) => { setFStatus(e.target.value); setPage(1); }} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option value="">All Status</option><option value="active">Active</option><option value="disabled">Disabled</option>
          </select>
        </div>
      </Card>

      <Card testId="emp-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Employee</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Process</th>
                <th className="px-4 py-3">Designation</th><th className="px-4 py-3">Manager</th><th className="px-4 py-3">Shift</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Last Login</th>{isAdmin && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && <tr><td colSpan={9}><Empty /></td></tr>}
              {paged.map((u) => (
                <tr key={u.id} data-testid={`emp-row-${u.employee_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 font-semibold text-[#0B1F33]">{u.name} {u.is_demo && <DemoTag />}</div>
                    <div className="text-xs text-slate-400">{u.employee_id} · {u.email}</div>
                  </td>
                  <td className="px-4 py-3"><Pill tone={u.role === "admin" ? "red" : u.role.includes("manager") ? "teal" : "slate"}>{u.role.replace("_", " ")}</Pill></td>
                  <td className="px-4 py-3 text-slate-600">{u.process || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{u.designation || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{u.manager_name || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{u.shift || "—"}</td>
                  <td className="px-4 py-3"><Pill tone={u.employment_status === "active" ? "green" : "slate"}>{u.employment_status}</Pill></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(u.last_login)}</td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`edit-${u.employee_id}`} onClick={() => setModal({ mode: "edit", data: { ...empty, ...u } })} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-[#08A7A0]" title="Edit"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`pw-${u.employee_id}`} onClick={() => setPwModal({ id: u.id, pw: "GoSource@123" })} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-amber-600" title="Reset password"><KeyRound className="h-4 w-4" /></button>
                        <button data-testid={`toggle-${u.employee_id}`} onClick={() => toggle(u)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title={u.employment_status === "active" ? "Disable" : "Reactivate"}>
                          {u.employment_status === "active" ? <Ban className="h-4 w-4 text-red-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                        </button>
                        <button data-testid={`delete-${u.employee_id}`} onClick={() => setDelModal(u)} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" title="Delete employee"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm">
          <span className="text-slate-500">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold disabled:opacity-40">Prev</button>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold disabled:opacity-40">Next</button>
          </div>
        </div>
      </Card>

      {modal && <EmployeeModal modal={modal} setModal={setModal} opts={opts} save={save} />}
      {pwModal && (
        <Modal title="Reset Password" onClose={() => setPwModal(null)}>
          <label className="text-xs font-semibold text-slate-500">New password</label>
          <input data-testid="reset-pw-input" value={pwModal.pw} onChange={(e) => setPwModal({ ...pwModal, pw: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <button data-testid="reset-pw-save" onClick={resetPw} className="mt-4 w-full rounded-lg bg-[#08A7A0] py-2.5 text-sm font-bold text-white">Reset Password</button>
        </Modal>
      )}
      {delModal && (
        <Modal title="Delete Employee" onClose={() => setDelModal(null)}>
          <p className="text-sm text-slate-600">This permanently deletes <span className="font-bold text-[#0B1F33]">{delModal.name}</span> ({delModal.employee_id}) and all of their attendance, break and correction records. This action cannot be undone.</p>
          <div className="mt-4 flex gap-2">
            <button data-testid="confirm-delete" onClick={deleteUser} className="flex-1 rounded-lg bg-red-600 py-2.5 text-sm font-bold text-white hover:bg-red-700">Delete Permanently</button>
            <button onClick={() => setDelModal(null)} className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600">Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full ${wide ? "max-w-2xl" : "max-w-md"} rounded-2xl bg-white p-6 shadow-xl`}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-[#0B1F33]">{title}</h3>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#08A7A0]";

function EmployeeModal({ modal, setModal, opts, save }) {
  const f = modal.data;
  const set = (k, v) => setModal({ ...modal, data: { ...f, [k]: v } });
  return (
    <Modal title={modal.mode === "add" ? "Add New Employee" : `Edit ${f.name}`} onClose={() => setModal(null)} wide>
      <div className="grid max-h-[70vh] grid-cols-1 gap-3 overflow-y-auto pr-1 sm:grid-cols-2">
        <Field label="Employee ID"><input data-testid="f-empid" disabled={modal.mode === "edit"} value={f.employee_id} onChange={(e) => set("employee_id", e.target.value)} className={inputCls} /></Field>
        <Field label="Full Name"><input data-testid="f-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Field>
        <Field label="Email"><input data-testid="f-email" disabled={modal.mode === "edit"} value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></Field>
        {modal.mode === "add" && <Field label="Temp Password"><input data-testid="f-pw" value={f.temp_password} onChange={(e) => set("temp_password", e.target.value)} className={inputCls} /></Field>}
        <Field label="Role"><select data-testid="f-role" value={f.role} onChange={(e) => set("role", e.target.value)} className={inputCls}><option value="employee">Employee</option><option value="team_lead">Team Lead</option><option value="manager">Manager</option><option value="senior_manager">Senior Manager</option><option value="admin">Admin</option></select></Field>
        <Field label="Process"><select data-testid="f-process" value={f.process || ""} onChange={(e) => set("process", e.target.value)} className={inputCls}><option value="">—</option>{opts.processes.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}</select></Field>
        <Field label="Designation"><select data-testid="f-designation" value={f.designation || ""} onChange={(e) => set("designation", e.target.value)} className={inputCls}><option value="">—</option>{opts.designations.map((d) => <option key={d} value={d}>{d}</option>)}</select></Field>
        <Field label="Manager"><select data-testid="f-manager" value={f.manager_id || ""} onChange={(e) => set("manager_id", e.target.value)} className={inputCls}><option value="">—</option>{opts.managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></Field>
        <Field label="Team Lead"><select data-testid="f-teamlead" value={f.team_lead_id || ""} onChange={(e) => set("team_lead_id", e.target.value)} className={inputCls}><option value="">—</option>{opts.team_leads.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></Field>
        <Field label="Shift"><select data-testid="f-shift" value={f.shift || ""} onChange={(e) => set("shift", e.target.value)} className={inputCls}><option value="">—</option>{opts.shifts.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}</select></Field>
        <Field label="Location"><input data-testid="f-location" value={f.location || ""} onChange={(e) => set("location", e.target.value)} className={inputCls} /></Field>
        <Field label="Date of Joining"><input data-testid="f-doj" type="date" value={f.doj || ""} onChange={(e) => set("doj", e.target.value)} className={inputCls} /></Field>
        <Field label="Mobile"><input data-testid="f-mobile" value={f.mobile || ""} onChange={(e) => set("mobile", e.target.value)} className={inputCls} /></Field>
        <Field label="Status"><select data-testid="f-status" value={f.employment_status} onChange={(e) => set("employment_status", e.target.value)} className={inputCls}><option value="active">Active</option><option value="disabled">Disabled</option></select></Field>
      </div>
      <button data-testid="save-employee" onClick={save} className="mt-4 w-full rounded-lg bg-[#08A7A0] py-2.5 text-sm font-bold text-white hover:bg-[#06827C]">{modal.mode === "add" ? "Create Employee" : "Save Changes"}</button>
    </Modal>
  );
}
